import React, { useEffect, useState, useCallback } from 'react';
import API from '../../api';
import { Database, Download, RefreshCw, Loader2, FileCheck2, FileQuestion, Eye, X } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DatasetImportTab() {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyConfirmed, setOnlyConfirmed] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await API.get('/api/import/samples', { params: { only_confirmed: onlyConfirmed, limit: 200 } });
      setSamples(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setSamples([]);
    } finally {
      setLoading(false);
    }
  }, [onlyConfirmed]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setLoadingDetail(true);
    setDetail({ id });
    try {
      const r = await API.get(`/api/import/samples/${id}`);
      setDetail(r.data);
    } catch (e) {
      setDetail({ error: e.response?.data?.detail || e.message });
    } finally {
      setLoadingDetail(false);
    }
  };

  const exportJsonl = async () => {
    // Esporta tutti i sample CONFERMATI come JSONL (uno per riga) per fine-tuning
    const r = await API.get('/api/import/samples', { params: { only_confirmed: true, limit: 1000 } });
    const lines = (r.data || []).map(s => JSON.stringify({
      input_text: s.extracted_text || '',
      output_voci: s.confirmed_voci || s.ai_voci || [],
      filename: s.filename_original,
      tipo: s.tipo,
    }));
    const blob = new Blob([lines.join('\n')], { type: 'application/jsonl' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import_dataset_${new Date().toISOString().slice(0,10)}.jsonl`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const confirmedCount = samples.filter(s => s.confirmed_voci).length;

  return (
    <div className="space-y-4" data-testid="dataset-import-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Database className="text-primary" size={20} />
          <h2 className="text-lg font-bold">Dataset Import (training)</h2>
          <span className="text-xs text-slate-500">{samples.length} sample · {confirmedCount} confermati</span>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={onlyConfirmed} onChange={e => setOnlyConfirmed(e.target.checked)} />
            Solo confermati
          </label>
          <button onClick={exportJsonl} disabled={confirmedCount === 0}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded text-sm font-bold hover:bg-purple-700 disabled:opacity-40"
            data-testid="dataset-export-btn">
            <Download size={14} /> Esporta JSONL ({confirmedCount})
          </button>
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded text-sm font-bold disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Ricarica
          </button>
        </div>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded p-3 text-xs text-purple-800">
        💡 Ogni volta che un utente importa un documento (Computo/Preventivo) tramite "Importa con AI", il file e l'output del modello vengono salvati qui.
        Le voci finali confermate (eventualmente editate) sono il <strong>ground truth</strong>. Esporta in JSONL per fine-tuning o miglioramento del prompt.
      </div>

      <div className="border border-slate-200 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500">File</th>
              <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500">Tipo</th>
              <th className="text-center px-3 py-2 text-xs uppercase font-bold text-slate-500">Voci AI</th>
              <th className="text-center px-3 py-2 text-xs uppercase font-bold text-slate-500">Stato</th>
              <th className="text-left px-3 py-2 text-xs uppercase font-bold text-slate-500">Data</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center text-slate-400 py-8">Caricamento…</td></tr>
            ) : samples.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-slate-400 py-8">Nessun sample. Importa un computo o preventivo dalla pagina principale per creare i primi sample.</td></tr>
            ) : (
              samples.map(s => (
                <tr key={s._id || s.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">{s.filename_original}</td>
                  <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 bg-slate-100 rounded font-bold">{s.tipo}</span></td>
                  <td className="px-3 py-2 text-center text-xs">{(s.ai_voci || []).length}</td>
                  <td className="px-3 py-2 text-center">
                    {s.confirmed_voci ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700"><FileCheck2 size={12} /> Confermato</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500"><FileQuestion size={12} /> Solo AI</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">{s.created_at ? new Date(s.created_at).toLocaleString('it-IT') : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openDetail(s._id || s.id)} className="text-primary hover:text-primary-hover"
                      data-testid={`dataset-detail-${s._id || s.id}`} title="Visualizza">
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-bold">Dettaglio Sample {detail.filename_original ? `· ${detail.filename_original}` : ''}</h3>
              <button onClick={() => setDetail(null)}><X size={20} className="text-slate-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loadingDetail ? <div className="flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Caricamento…</div> :
                detail.error ? <div className="text-red-600">{detail.error}</div> :
                <>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div><span className="font-bold">Tipo:</span> {detail.tipo}</div>
                    <div><span className="font-bold">Kind:</span> {detail.kind}</div>
                    <div><span className="font-bold">Voci AI:</span> {(detail.ai_voci || []).length}</div>
                    <div><span className="font-bold">Voci confermate:</span> {(detail.confirmed_voci || []).length}</div>
                    <div><span className="font-bold">Modello:</span> {detail.ai_model || '—'}</div>
                    <div><span className="font-bold">N. immagini:</span> {detail.n_images ?? 0}</div>
                  </div>

                  {detail.extracted_text && (
                    <details className="border rounded">
                      <summary className="px-3 py-2 cursor-pointer bg-slate-50 text-sm font-bold">Testo estratto ({detail.extracted_text.length} char)</summary>
                      <pre className="p-3 text-xs whitespace-pre-wrap max-h-64 overflow-y-auto bg-white">{detail.extracted_text}</pre>
                    </details>
                  )}

                  <div>
                    <p className="text-sm font-bold mb-2">Voci AI (proposte iniziali):</p>
                    <VoceTable voci={detail.ai_voci || []} />
                  </div>

                  {detail.confirmed_voci && (
                    <div>
                      <p className="text-sm font-bold mb-2 text-green-700">Voci confermate (ground truth):</p>
                      <VoceTable voci={detail.confirmed_voci} />
                    </div>
                  )}

                  {detail.filename_stored && (
                    <a href={`${BACKEND}/uploads/import_samples/${detail.filename_stored}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline">
                      <Download size={14} /> Scarica file originale
                    </a>
                  )}
                </>
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VoceTable({ voci }) {
  if (!voci || voci.length === 0) return <p className="text-xs text-slate-400 italic">Nessuna voce</p>;
  return (
    <table className="w-full text-xs border border-slate-200 rounded">
      <thead className="bg-slate-50">
        <tr>
          <th className="text-left px-2 py-1">Descrizione</th>
          <th className="text-right px-2 py-1">Qtà</th>
          <th className="text-left px-2 py-1">U.M.</th>
          <th className="text-right px-2 py-1">P.U.</th>
          <th className="text-right px-2 py-1">Importo</th>
        </tr>
      </thead>
      <tbody>
        {voci.map((v, i) => (
          <tr key={i} className="border-t border-slate-100">
            <td className="px-2 py-1">{v.descrizione}</td>
            <td className="px-2 py-1 text-right">{v.quantita ?? '—'}</td>
            <td className="px-2 py-1">{v.unita_misura ?? '—'}</td>
            <td className="px-2 py-1 text-right font-mono">{v.prezzo_unitario != null ? Number(v.prezzo_unitario).toFixed(2) : '—'}</td>
            <td className="px-2 py-1 text-right font-mono font-bold">{v.importo != null ? Number(v.importo).toFixed(2) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
